export interface ParsedMedicalSummarySections {
  clinicalSummary: string;
  riskSnapshot: string;
  keyConcern: string;
  watch: string;
  hasStructuredSections: boolean;
}

/**
 * Extracts content following a heading div until the next heading or end.
 */
function extractSection(html: string, headingLabel: string): string {
  const escaped = headingLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(
    `<div[^>]*font-weight:\\s*600[^>]*>\\s*${escaped}\\s*:<\\/div>([\\s\\S]*?)(?=<div[^>]*font-weight:\\s*600[^>]*>|$)`,
    "i"
  );
  const match = html.match(regex);
  if (!match) return "";
  return match[1].trim();
}

function joinSections(html: string, labels: string[]): string {
  const parts = labels
    .map((label) => extractSection(html, label))
    .filter((part) => part.length > 0);
  return parts.join("<br /><br />");
}

/**
 * Maps server-generated medical summary HTML into AI Overview sidebar sections.
 */
export function parseMedicalSummaryHtml(
  html: string
): ParsedMedicalSummarySections {
  if (!html || html.trim() === "") {
    return {
      clinicalSummary: "",
      riskSnapshot: "",
      keyConcern: "",
      watch: "",
      hasStructuredSections: false,
    };
  }

  const topCritical = extractSection(html, "Top Critical Alerts");
  const riskStratification = extractSection(html, "Risk Stratification");
  const provisionalDiagnosis = extractSection(html, "Provisional Diagnosis");
  const advicePlan = extractSection(html, "Advice & Clinical Plan");

  const clinicalSummary = joinSections(html, [
    "Patient Demographics",
    "Vital Signs",
    "Clinical Presentation",
  ]);

  let riskSnapshot = riskStratification;
  if (!riskSnapshot && topCritical) {
    riskSnapshot = topCritical;
  }

  let keyConcern = provisionalDiagnosis;
  if (!keyConcern && topCritical) {
    keyConcern = topCritical;
  }

  const watch = advicePlan;

  const hasStructuredSections = Boolean(
    clinicalSummary || riskSnapshot || keyConcern || watch
  );

  return {
    clinicalSummary,
    riskSnapshot,
    keyConcern,
    watch,
    hasStructuredSections,
  };
}
